import { SalesforceField } from '@/model/SalesforceField';
import { useState, memo, ChangeEvent, FormEvent } from 'react';
import { useI18n } from 'next-localization';
import { useFormContext } from 'components/Generic/Forms/SalesforceIntegratedForms/FormContext';

const SalesforceInput = memo(function SalesforceInput({ fields, stepName }: SalesforceField) {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { formData, updateFormData, updateSummary } = useFormContext();
  const fieldId = fields.salesforceFieldId.value;
  const inputValue = formData.records[0][fieldId] || '';
  const { t } = useI18n();

  const resetInputValue = (): void => {
    updateFormData({
      fieldName: fields.salesforceFieldId.value,
      value: '',
    });
  };

  const setValue = (e: ChangeEvent<HTMLInputElement>) => {
    updateFormData({
      fieldName: fields.salesforceFieldId.value,
      value: e.target.value,
    });
    updateSummary({
      stepName,
      fieldName: {
        storeName: fields.salesforceFieldId.value,
        displayName: fields.title.value,
      },
      fieldValue: e.target.value,
    });
  };

  const checkError = (event: FormEvent<HTMLInputElement>) => {
    const inputElement = event.target as HTMLInputElement;
    switch (true) {
      case inputElement.validity.typeMismatch:
        setErrorMessage('Please enter correct email address.');
        setIsError(true);
        break;
      case fields.isRequired.value === true && inputElement.value.trim() === '':
        setErrorMessage(`Please enter the ${fields.title.value}`);
        setIsError(true);
        break;
      case inputElement.validity.valueMissing:
        setErrorMessage(`Please enter the ${fields.title.value}`);
        setIsError(true);
        break;
      case inputElement.validity.customError:
        setIsError(true);
        break;
    }
  };

  return (
    <div className={`mb-4 salesforce-input ${isError ? 'form-element--error' : ''}`}>
      <label htmlFor={fields.salesforceFieldId.value} className="mb-1.5 form-label">
        {fields.title.value} {fields.isRequired.value === true ? '*' : ''}
      </label>
      <div className="input-box" style={{ maxWidth: 480 }}>
        <input
          id={fields.salesforceFieldId.value}
          data-id={fields.salesforceFieldId.value}
          type={fields.type.value}
          value={inputValue}
          className={`field-text ${isError ? 'field-error' : ''}`}
          required={fields.isRequired.value}
          onChange={setValue}
          onInput={() => setIsError(false)}
          onBlur={checkError}
          onInvalid={checkError}
        />
        {inputValue && !isError && (
          <button
            type="button"
            className="input-action-btn input-icon"
            aria-label={t('clear')}
            onClick={resetInputValue}
          >
            <i className="bi bi-x" />
          </button>
        )}
        {isError && (
          <span className="input-icon input-icon--right input-icon--error">
            <i className="bi bi-exclamation-triangle" />
          </span>
        )}
      </div>
      {isError && <span className="field-error-text text-error-400">{errorMessage}</span>}
    </div>
  );
});

export default SalesforceInput;
